import { useMutation, useReactiveVar } from '@apollo/client';
import { Typography } from '@mui/material';
import { useLocation, useNavigate } from '@tanstack/react-router';
import type React from 'react';
import deleteAssets from 'src/api/assets/deleteAssets';
import deleteWholeCollection from 'src/api/collection/deleteWholeCollection';
import { COLLECTION } from 'src/constants';
import { useCustomToast } from 'src/hooks/menu';
import viewStateVar from 'src/store/viewState';
import AkomiDialog from './AkomiDialog';
import AkomiDialogActions from './AkomiDialogActions';

interface DeleteDialogProps {
  refetchQ: string;
}

interface DeleteAssetVariables {
  assetId: string;
}

interface DeleteCollectionVariables {
  collectionId: string;
}

const DeleteDialog: React.FC<DeleteDialogProps> = ({ refetchQ }) => {
  const { deleteDialog, selectedAssets, shareContent } = useReactiveVar(viewStateVar);
  const _navigate = useNavigate();
  const location = useLocation();
  const { toastr } = useCustomToast();

  const [deleteAssetHandler] = useMutation<unknown, DeleteAssetVariables>(deleteAssets, {
    fetchPolicy: 'network-only',
    refetchQueries: [refetchQ],
  });

  const [deleteCollectionHandler] = useMutation<unknown, DeleteCollectionVariables>(deleteWholeCollection, {
    fetchPolicy: 'network-only',
    refetchQueries: [refetchQ],
  });

  const deleteAssetLibraryHandler = async (): Promise<void> => {
    // Handle collection deletion
    if (shareContent === COLLECTION && deleteDialog) {
      try {
        await deleteCollectionHandler({
          variables: {
            collectionId: deleteDialog as string,
          },
        });
        toastr.success('Gallery was successfully deleted');
      } catch (error) {
        console.error('Error deleting gallery:', error);
        toastr.error('Error deleting gallery');
      }
    }
    // Handle asset deletion
    else if (selectedAssets && selectedAssets.length > 0) {
      await Promise.all(
        selectedAssets.map((assetId) => {
          return deleteAssetHandler({
            variables: {
              assetId,
            },
          })
            .then(() => {
              toastr.success('Asset was successfully deleted');
            })
            .catch((error) => {
              console.error('Error deleting asset:', error);
              toastr.error('Error deleting asset');
            });
        }),
      );
    }

    viewStateVar({
      ...viewStateVar(),
      deleteDialog: null,
      selectedAssets: [],
      shareContent: null,
    });
    // Only if we are on media page then go to library after deleting the asset
    if (location.pathname.includes('media-page')) {
      window.history.back();
    }
  };

  const cancelDeleteHandler = (): void => {
    viewStateVar({
      ...viewStateVar(),
      deleteDialog: null,
      shareContent: null,
    });
  };

  return (
    <AkomiDialog
      title="Delete"
      maxWidth="xs"
      open={!!deleteDialog}
      onClose={cancelDeleteHandler}
      actions={
        <AkomiDialogActions
          variant="delete-cancel"
          primaryLabel="Delete"
          onPrimary={deleteAssetLibraryHandler}
          onSecondary={cancelDeleteHandler}
        />
      }
    >
      <Typography>
        {shareContent === COLLECTION
          ? 'Are you sure you want to delete this gallery?'
          : selectedAssets.length === 1
            ? 'Are you sure you want to delete this item?'
            : `Are you sure you want to delete these ${selectedAssets.length} items?`}
      </Typography>
    </AkomiDialog>
  );
};

export default DeleteDialog;
